"use client";

import { type ReactNode, useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { StepLoader } from "@/components/loading/step-loader";
import { useReportStepProgress } from "@/components/navigation/stepper";
import { useStepTransition } from "@/components/transitions/transition-context";
import { ProcessType, TaskType } from "@/types";

interface StepContainerProps {
	children: ReactNode;
	/** Vertical spacing between children, in 8px spacing steps. */
	gap?: number;
	className?: string;
	/**
	 * The step to show in the flow layout's progress header. Omit for steps that
	 * are not part of the stepper (login, sign, approved, wallet).
	 */
	taskType?: TaskType;
	processType?: ProcessType;
}

const TRANSITION = { duration: 0.25, ease: "easeOut" } as const;

/**
 * Hiding the content blurs whatever had focus, so a transition that ends
 * without navigating hands focus back instead of dropping it on `<body>`.
 */
const useRestoreFocusAfterTransition = (isTransitioning: boolean) => {
	const lastFocusedRef = useRef<HTMLElement | null>(null);
	const wasTransitioningRef = useRef(isTransitioning);

	useEffect(() => {
		const wasTransitioning = wasTransitioningRef.current;
		wasTransitioningRef.current = isTransitioning;
		const target = lastFocusedRef.current;
		if (!wasTransitioning || isTransitioning || !target) return;

		const frame = requestAnimationFrame(() => {
			const active = document.activeElement;
			if (target.isConnected && (!active || active === document.body)) {
				target.focus();
			}
		});
		return () => cancelAnimationFrame(frame);
	}, [isTransitioning]);

	return (element: EventTarget) => {
		if (element instanceof HTMLElement) {
			lastFocusedRef.current = element;
		}
	};
};

/**
 * Wraps a step's content inside the card frame owned by the flow layout. While
 * a step submit is in flight the content fades out and a loader takes its
 * place. The content stays mounted, so a submit that fails returns the customer
 * to their input.
 */
export const StepContainer = ({
	children,
	gap = 3,
	className = "",
	taskType,
	processType,
}: StepContainerProps) => {
	const { isTransitioning } = useStepTransition();
	const [fadedOut, setFadedOut] = useState(false);
	const trackFocus = useRestoreFocusAfterTransition(isTransitioning);
	useReportStepProgress({ taskType, processType });

	if (!isTransitioning && fadedOut) {
		setFadedOut(false);
	}
	const hideContent = isTransitioning && fadedOut;

	return (
		<div className={`w-full ${className}`}>
			{hideContent && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={TRANSITION}
				>
					<StepLoader />
				</motion.div>
			)}
			<motion.div
				initial={{ opacity: 0, y: 8 }}
				animate={isTransitioning ? { opacity: 0, y: -8 } : { opacity: 1, y: 0 }}
				transition={TRANSITION}
				onAnimationComplete={() => {
					if (isTransitioning) setFadedOut(true);
				}}
				onFocus={(event) => trackFocus(event.target)}
				inert={isTransitioning}
				style={{
					display: hideContent ? "none" : "flex",
					flexDirection: "column",
					gap: `calc(var(--lea-spacing) * ${gap})`,
					width: "100%",
				}}
			>
				{children}
			</motion.div>
		</div>
	);
};
